import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  collection,
  collectionSnapshots,
  doc,
  docSnapshots,
  Firestore,
  query,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { AnalysisData } from '@sleipai/sleip-models';
import { from } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

@Injectable()
export class ReviewService {
  constructor(private http: HttpClient) {}

  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private storage = inject(Storage);
  private destroyRef = inject(DestroyRef);

  getSingleReview(id: string) {
    const analysisDataRef = doc(this.firestore, `analysisData/${id}`);
    return docSnapshots(analysisDataRef).pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
      map((snap) => {
        return {
          id: snap.id,
          ...snap.data(),
        } as AnalysisData;
      })
    );
  }

  getAllPendingReviews() {
    const analysisDataCollectionRef = collection(
      this.firestore,
      'analysisData'
    );
    const q = query(
      analysisDataCollectionRef,
      where('status', '==', 'pending')
    );
    return collectionSnapshots(q).pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
      map((snap) => {
        return snap.map((analysis) => {
          return {
            id: analysis.id,
            ...analysis.data(),
          } as AnalysisData;
        });
      })
    );
  }

  getReviewData(path: string) {
    const reviewDataFileRef = ref(this.storage, path);
    return from(getDownloadURL(reviewDataFileRef)).pipe(
      switchMap((url) => {
        return this.http.get(url);
      })
    );
  }

  updateAnalysisDataObject(id: string, data: any) {
    return updateDoc(doc(this.firestore, `analysisData/${id}`), data)
      .then(() => {
        return true;
      })
      .catch(() => {
        return false;
      });
  }

  acceptMimirReview(reviewData: any) {
    if (reviewData.type === 'decline') {
      delete reviewData.strideLists;
    }
    const callable = httpsCallable(this.functions, 'reviewApi-acceptReview');
    return from(callable(reviewData)).pipe(map((response) => response.data));
  }
}
